import { useLocation } from 'react-router-dom';

import { enforceSafeCloudId } from '@/utils/enforce-safe-cloud-id';

import { DirectBuyStatus } from '../post-order/src/components/types';

export type SearchParameters = {
	readonly cloudId: string;
	readonly requestId: string;
	readonly products: readonly string[];
	readonly bitbucketWorkspaceUuid?: string;
	readonly referrer?: string;
	readonly onDemandUrl: string;
	readonly siteRenameRequestId?: string;
	readonly isClaiming?: boolean;
	readonly locale?: string;
	readonly provisioningRequestId?: string;

	// tintin
	readonly platform: 'ccp' | 'hams' | 'routing';

	// for redirect to instance
	readonly dataRegion?: string;
	readonly admin?: boolean;

	// devops bundle land in code
	readonly signupSource?: string;
	readonly isFoundingAdmin?: string;

	// redirect to WAC App Provisioning
	readonly provisionApp?: string;

	// hide user segmentation screen
	readonly hideUserSegmentation?: string;

	// appKey for installing 2p app after provisioning on confirmation page
	readonly appKey?: string;

	// auto-generated site name
	readonly templateId?: string;

	// AI-led onboarding identifier
	readonly aiLedOnboarding?: string;

	readonly edition?: string;

	// Project Cobra (Arcadia Platform Cut Sign Up Steps in Half)
	readonly asp?: string;

	// determines if this signup is a n2n user
	readonly n2n: boolean;

	// maintains template selection on refresh (confirmation page)
	readonly templateChoice?: string;

	// maintains jtbd template override selection on refresh (confirmation page)
	readonly jtbdTemplateOverride?: string;

	// GraphQL experiment
	readonly orchestrationId?: string;

	// Jira Familiarity, part of Move Project Name Screen experiment JFIND-130
	readonly jf?: string;

	// team type for user segmentation
	readonly teamType?: string;

	// Jobs To Be Done
	// https://hello.atlassian.net/wiki/spaces/Spork/pages/3820424905/Tailored+Onboarding+-+Personalised+Landing+based+on+JTBD
	readonly jtbd?: string;
	readonly jtbdPersona?: string;

	// Coding agent choice (TWCG-1935)
	readonly codingAgents?: string;

	// continue url for contact expand
	readonly continueUrl?: string;

	// org Id for provision on org
	readonly orgId?: string;

	// activateFor for direct buy
	readonly activateFor?: string;
	readonly orderId: string;

	// workflowId for async architecture site activation
	readonly workflowId?: string;

	// signupId created in the signup flow
	readonly signupId?: string;

	// the product to be redirected to if user signed up for collection
	readonly primaryCollectionProduct: string;
	readonly siteName?: string;
	readonly spaceName?: string;
	readonly taskIdParam?: string;
	readonly transactionAccount: string;
	readonly directBuyStatus: DirectBuyStatus;
};

/**
 * @returns {URLSearchParams} The search (query) parameters from the URL.
 *
 * This function is used to get the search parameters from the URL.
 * If in a router context, it uses the {@link useLocation} hook,
 * otherwise it uses the global location.
 */
const getSearchParameters = (): URLSearchParams => {
	try {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		return new URLSearchParams(useLocation().search);
	} catch {
		return new URLSearchParams(location.search);
	}
};

// eslint-disable-next-line complexity
export function useSearchParameters(): SearchParameters {
	const searchParameters = getSearchParameters();
	const cloudId = enforceSafeCloudId(searchParameters.get('cloudId'));
	const requestId = searchParameters.get('requestId') ?? '';
	const products = searchParameters.get('products') ?? '';
	const bitbucketWorkspaceUuid = searchParameters.get('bitbucketWorkspaceUuid') ?? undefined;
	const referrer = searchParameters.get('referrer') ?? undefined;
	const isClaiming = searchParameters.get('claim') === 'true';
	const dataRegion = searchParameters.get('dataRegion') ?? undefined;
	let onDemandUrl = searchParameters.get('ondemandurl') ?? '';
	// this is site name - added as a security vuln
	onDemandUrl = onDemandUrl?.includes('.') ? '' : onDemandUrl;
	const admin = searchParameters.get('admin') === 'true';
	const siteRenameRequestId = searchParameters.get('siteRenameRequestId') ?? undefined;
	const locale = searchParameters.get('locale') ?? '';
	const platformParam = searchParameters.get('platform');
	const platform = platformParam === 'ccp' || platformParam === 'hams' ? platformParam : 'routing';
	const provisioningRequestId = searchParameters.get('provisioningRequestId') ?? undefined;
	const signupSource = searchParameters.get('signupSource') ?? undefined;
	const isFoundingAdmin = searchParameters.get('isFoundingAdmin') ?? undefined;
	const provisionApp = searchParameters.get('provisionApp') ?? undefined;
	const hideUserSegmentation = searchParameters.get('hideUserSegmentation') ?? undefined;
	const appKey = searchParameters.get('appKey') ?? undefined;
	const templateId = searchParameters.get('templateId') ?? undefined;
	const templateChoice = searchParameters.get('templateChoice') ?? undefined;
	const jtbdTemplateOverride = searchParameters.get('jtbdTemplateOverride') ?? undefined;
	const aiLedOnboarding = searchParameters.get('aiLedOnboarding') ?? undefined;
	const edition = searchParameters.get('edition') ?? undefined;
	const asp = searchParameters.get('asp') ?? '';
	const n2n = searchParameters.get('n2n') === 'true';
	const orchestrationId = searchParameters.get('orchestrationId') ?? undefined;
	const jf = searchParameters.get('jf') ?? undefined;
	const teamType = searchParameters.get('team_type') ?? undefined;
	const jtbd = searchParameters.get('jtbd') ?? undefined;
	const jtbdPersona = searchParameters.get('jtbdPersona') ?? undefined;
	const codingAgents = searchParameters.get('coding_agents') ?? undefined;
	const continueUrl = searchParameters.get('continueUrl') ?? undefined;
	const orgId = searchParameters.get('orgId') ?? '';
	const activateFor = searchParameters.get('activateFor') ?? '';
	const orderId = searchParameters.get('orderId') ?? '';
	const workflowId = searchParameters.get('workflowId') ?? undefined;
	const primaryCollectionProduct = searchParameters.get('primaryCollectionProduct') ?? '';
	const signupId = searchParameters.get('signupId') ?? undefined;
	const siteName = searchParameters.get('siteName') ?? undefined;
	const spaceName = searchParameters.get('spaceName') ?? undefined;
	const taskIdParam = searchParameters.get('taskId') ?? undefined;
	const transactionAccount = searchParameters.get('transactionAccount') ?? '';
	const directBuyStatus = (searchParameters.get('directBuyStatus') as DirectBuyStatus) ?? '';

	return {
		activateFor,
		admin,
		appKey,
		asp,
		bitbucketWorkspaceUuid,
		cloudId,
		continueUrl,
		dataRegion,
		edition,
		hideUserSegmentation,
		isClaiming,
		isFoundingAdmin,
		jf,
		jtbd,
		jtbdPersona,
		codingAgents,
		locale,
		n2n,
		onDemandUrl,
		orchestrationId,
		orderId,
		orgId,
		platform,
		primaryCollectionProduct,
		products: products.split(','),
		provisionApp,
		provisioningRequestId,
		referrer,
		requestId,
		signupId,
		signupSource,
		siteRenameRequestId,
		teamType,
		templateChoice,
		templateId,
		jtbdTemplateOverride,
		aiLedOnboarding,
		workflowId,
		siteName,
		spaceName,
		taskIdParam,
		transactionAccount,
		directBuyStatus,
	};
}
